/**
 * AME Lift Chair Configurator — guided quiz for lift chair / power recliner selection
 * ES5 IIFE, no dependencies, deferred loading
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-lc-cfg]');
  if (!root) return;

  // --- DOM refs ---
  var ctaBtn = root.querySelector('[data-lc-cfg-cta]');
  var wizard = root.querySelector('#ame-lc-cfg-wizard');
  var resultsPanel = root.querySelector('[data-lc-cfg-results]');
  var resultsGrid = root.querySelector('[data-lc-cfg-results-grid]');
  var resultsHeading = root.querySelector('[data-lc-cfg-results-heading]');
  var resultsSubtitle = root.querySelector('[data-lc-cfg-results-subtitle]');
  var viewAllLink = root.querySelector('[data-lc-cfg-view-all]');
  var progressLabel = root.querySelector('[data-lc-cfg-progress-label]');
  var progressBar = root.querySelector('[data-lc-cfg-progressbar]');
  var progressFill = root.querySelector('[data-lc-cfg-progress-fill]');
  var liveRegion = root.querySelector('[data-lc-cfg-live]');

  var steps = root.querySelectorAll('[data-lc-cfg-step]');
  var TOTAL_STEPS = steps.length;

  // --- State ---
  var currentStep = 1;
  var answers = {};
  var products = [];

  // --- Parse product data ---
  var dataEl = document.getElementById('ame-lc-cfg-data');
  if (dataEl) {
    try {
      products = JSON.parse(dataEl.textContent);
    } catch (e) {
      products = [];
    }
  }

  // --- Session storage helpers ---
  function saveState() {
    try {
      sessionStorage.setItem('ame-lc-cfg-state', JSON.stringify({
        step: currentStep,
        answers: answers
      }));
    } catch (e) { /* ignore */ }
  }

  function loadState() {
    try {
      var raw = sessionStorage.getItem('ame-lc-cfg-state');
      if (raw) return JSON.parse(raw);
    } catch (e) { /* ignore */ }
    return null;
  }

  function clearState() {
    try { sessionStorage.removeItem('ame-lc-cfg-state'); } catch (e) { /* ignore */ }
  }

  // --- Announce to screen readers ---
  function announce(msg) {
    if (liveRegion) liveRegion.textContent = msg;
  }

  // --- Progress bar ---
  function updateProgress(step) {
    var pct = Math.round((step / TOTAL_STEPS) * 100);
    if (progressLabel) progressLabel.textContent = 'Step ' + step + ' of ' + TOTAL_STEPS;
    if (progressFill) progressFill.style.width = pct + '%';
    if (progressBar) {
      progressBar.setAttribute('aria-valuenow', pct);
    }
  }

  // --- Check if step is multi-select ---
  function isMultiStep(stepEl) {
    return stepEl.hasAttribute('data-lc-cfg-multi');
  }

  // --- Show/hide steps ---
  function showStep(n) {
    for (var i = 0; i < steps.length; i++) {
      steps[i].classList.toggle('is-active', i === n - 1);
    }
    currentStep = n;
    updateProgress(n);

    var stepEl = steps[n - 1];

    if (isMultiStep(stepEl)) {
      // Restore checkboxes
      var checkboxes = stepEl.querySelectorAll('input[type="checkbox"]');
      for (var c = 0; c < checkboxes.length; c++) {
        checkboxes[c].checked = !!answers[checkboxes[c].name];
      }
    } else {
      // Restore radio
      var radios = stepEl.querySelectorAll('input[type="radio"]');
      var fieldName = radios.length > 0 ? radios[0].name : '';
      if (fieldName && answers[fieldName]) {
        for (var j = 0; j < radios.length; j++) {
          radios[j].checked = radios[j].value === answers[fieldName];
        }
      }
    }

    updateNextBtn(stepEl);

    // Focus management
    var question = stepEl.querySelector('.ame-lc-cfg__question');
    if (question) question.focus();

    announce('Step ' + n + ' of ' + TOTAL_STEPS);
    saveState();
  }

  // --- Next button enabled/disabled ---
  function updateNextBtn(stepEl) {
    var nextBtn = stepEl.querySelector('[data-lc-cfg-next]');
    if (!nextBtn) return;

    if (isMultiStep(stepEl)) {
      // Multi-select steps always allow proceeding (0 selections = "just basics")
      nextBtn.disabled = false;
    } else {
      var checked = stepEl.querySelector('input[type="radio"]:checked');
      nextBtn.disabled = !checked;
    }
  }

  // --- Open wizard ---
  function openWizard() {
    wizard.hidden = false;
    ctaBtn.setAttribute('aria-expanded', 'true');
    showStep(currentStep);
    requestAnimationFrame(function () {
      var rect = wizard.getBoundingClientRect();
      var headerEl = document.querySelector('#shopify-section-header') || document.querySelector('.section-header');
      var headerH = headerEl ? headerEl.getBoundingClientRect().height : 0;
      window.scrollTo({ top: window.scrollY + rect.top - headerH - 16, behavior: 'smooth' });
    });
  }

  // --- Close wizard ---
  function closeWizard() {
    wizard.hidden = true;
    resultsPanel.classList.remove('is-active');
    ctaBtn.setAttribute('aria-expanded', 'false');
    ctaBtn.focus();
    currentStep = 1;
    answers = {};
    clearState();
  }

  // --- Collect answer from current step ---
  function collectAnswer() {
    var stepEl = steps[currentStep - 1];

    if (isMultiStep(stepEl)) {
      // Collect all checked checkboxes
      var checkboxes = stepEl.querySelectorAll('input[type="checkbox"]');
      for (var c = 0; c < checkboxes.length; c++) {
        if (checkboxes[c].checked) {
          answers[checkboxes[c].name] = checkboxes[c].value;
        } else {
          delete answers[checkboxes[c].name];
        }
      }
    } else {
      var checked = stepEl.querySelector('input[type="radio"]:checked');
      if (checked) {
        answers[checked.name] = checked.value;
      }
    }
  }

  // --- Tag helper ---
  function hasTag(tags, tag) {
    if (!tags || !tags.length) return false;
    for (var i = 0; i < tags.length; i++) {
      if (tags[i] === tag) return true;
    }
    return false;
  }

  // --- Scoring: Step 1 — Primary Need (weight: 3) ---
  function scoreNeed(product, need) {
    var tags = product.tags || [];
    var pt = (product.position_type || '').toLowerCase();
    var score = 0;

    switch (need) {
      case 'lift_assist':
        // Standard lift chair — any position type works
        if (hasTag(tags, 'lift-chair') || hasTag(tags, 'power-lift-recliner')) score += 6;
        if (hasTag(tags, 'lift-chair')) score += 2;
        break;
      case 'relaxation':
        // Comfortable recliner — prefer 3-Position or Infinite
        if (hasTag(tags, 'lift-chair') || hasTag(tags, 'recliner')) score += 4;
        if (pt === 'infinite position') score += 4;
        if (pt === '3-position') score += 3;
        if (product.heat_massage) score += 2;
        break;
      case 'sleep':
        // Must be Infinite Position (MaxiComfort/Twilight) for sleeping
        if (pt === 'infinite position') score += 10;
        if (pt === '3-position') score += 3;
        if (pt === '2-position') score -= 3;
        break;
      case 'recovery':
        // Surgery recovery — Infinite Position strongly preferred
        if (pt === 'infinite position') score += 10;
        if (pt === '3-position') score += 4;
        if (product.heat_massage) score += 2;
        if (pt === '2-position') score -= 2;
        break;
    }
    return score;
  }

  // --- Scoring: Step 2 — Position Type / Circulation (weight: 4) ---
  function scorePosition(product, position) {
    var pt = (product.position_type || '').toLowerCase();
    var score = 0;

    switch (position) {
      case 'full_recline':
        if (pt === 'infinite position') score += 10;
        if (pt === '3-position') score -= 2;
        if (pt === '2-position') score -= 6;
        break;
      case 'occasional':
        if (pt === 'infinite position') score += 8;
        if (pt === '3-position') score += 4;
        if (pt === '2-position') score -= 2;
        break;
      case 'standard':
        if (pt === '3-position') score += 6;
        if (pt === '2-position') score += 6;
        if (pt === 'infinite position') score += 3;
        break;
    }
    return score;
  }

  // --- Scoring: Step 3 — Body Size (weight: 3) ---
  function scoreBody(product, body) {
    var fs = (product.frame_size || '').toLowerCase();
    var cap = product.weight_capacity;
    var score = 0;

    switch (body) {
      case 'petite':
        if (fs === 'petite' || fs === 'compact') score += 8;
        if (fs === 'standard') score += 3;
        if (fs === 'tall' || fs === 'full-size' || fs === 'wide') score -= 3;
        if (cap !== null && cap < 200) score -= 100;
        if (cap !== null && cap >= 200) score += 4;
        break;
      case 'standard':
        if (fs === 'standard' || fs === 'mid-size') score += 8;
        if (fs === 'petite') score -= 3;
        if (fs === 'compact') score -= 1;
        if (cap !== null && cap < 325) score -= 100;
        if (cap !== null && cap >= 325) score += 4;
        break;
      case 'tall':
        if (fs === 'tall' || fs === 'full-size') score += 8;
        if (fs === 'standard' || fs === 'mid-size') score += 2;
        if (fs === 'petite' || fs === 'compact') score -= 5;
        if (cap !== null && cap < 375) score -= 100;
        if (cap !== null && cap >= 375) score += 4;
        break;
      case 'wide':
        if (fs === 'wide' || fs === 'full-size') score += 8;
        if (fs === 'tall') score += 2;
        if (fs === 'petite' || fs === 'compact') score -= 8;
        if (fs === 'standard' || fs === 'mid-size') score -= 3;
        if (cap !== null && cap < 400) score -= 100;
        if (cap !== null && cap >= 400) score += 4;
        break;
    }
    return score;
  }

  // --- Scoring: Step 4 — Comfort Features (weight: 2) ---
  function scoreFeatures(product, ans) {
    var score = 0;

    if (ans.lc_feat_heat === 'heat_massage') {
      if (product.heat_massage) score += 6;
      else score -= 3;
    }

    if (ans.lc_feat_wall === 'wall_hugger') {
      if (product.wall_hugger) score += 6;
      else score -= 2;
    }

    if (ans.lc_feat_battery === 'battery_backup') {
      if (product.battery_backup) score += 6;
      else score -= 2;
    }

    // "Just the basics" — no feature penalty
    if (ans.lc_feat_basic === 'basic') {
      score += 2;
    }

    return score;
  }

  // --- Scoring: Step 5 — Budget (weight: 1) ---
  function scoreBudget(product, budget) {
    if (product.call_for_price) return 5;

    var priceDollars = product.price / 100;

    if (!budget) return 5;

    var lo = 0;
    var hi = 0;

    switch (budget) {
      case '1000':
        lo = 0; hi = 1000;
        break;
      case '2000':
        lo = 1000; hi = 2000;
        break;
      case '3000':
        lo = 2000; hi = 3000;
        break;
      case '9999':
        lo = 3000; hi = 999999;
        break;
    }

    if (priceDollars >= lo && priceDollars <= hi) return 10;
    if (priceDollars <= hi * 1.2) return 5;
    return 0;
  }

  // --- Master scoring ---
  function scoreProducts(ans) {
    var need = ans.lc_need || 'lift_assist';
    var position = ans.lc_position || 'standard';
    var body = ans.lc_body || 'standard';
    var budget = ans.lc_budget || null;

    var scored = [];
    for (var i = 0; i < products.length; i++) {
      var p = products[i];

      // Skip non-lift-chair products (accessories, slings, etc.)
      if (!hasTag(p.tags, 'lift-chair') && !hasTag(p.tags, 'power-lift-recliner') && !hasTag(p.tags, 'recliner')) continue;

      var nScore = scoreNeed(p, need);
      var posScore = scorePosition(p, position);
      var bScore = scoreBody(p, body);
      var fScore = scoreFeatures(p, ans);
      var budScore = scoreBudget(p, budget);

      // Weighted total: need(3) + position(4) + body(3) + features(2) + budget(1)
      var total = (nScore * 3) + (posScore * 4) + (bScore * 3) + (fScore * 2) + (budScore * 1);

      scored.push({
        product: p,
        total: total,
        needScore: nScore,
        positionScore: posScore,
        bodyScore: bScore,
        featureScore: fScore,
        budgetScore: budScore
      });
    }

    scored.sort(function (a, b) { return b.total - a.total; });
    return scored;
  }

  // --- Match badge label ---
  function getMatchLabel(index) {
    switch (index) {
      case 0: return 'Best Match';
      case 1: return 'Great Fit';
      case 2: return 'Strong Pick';
      default: return 'Good Option';
    }
  }

  // --- Format price ---
  function formatPrice(cents) {
    var dollars = (cents / 100).toFixed(2);
    return '$' + dollars.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  // --- Build spec pills ---
  function buildSpecs(product) {
    var specs = [];
    if (product.position_type) specs.push(product.position_type);
    if (product.weight_capacity) specs.push(product.weight_capacity + ' lb capacity');
    if (product.heat_massage) specs.push('Heat & Massage');
    if (product.wall_hugger) specs.push('Wall Hugger');
    if (product.battery_backup) specs.push('Battery Backup');
    if (product.frame_size) specs.push(product.frame_size);
    return specs.slice(0, 4);
  }

  // --- HTML escaping ---
  function escapeHtml(str) {
    if (!str) return '';
    var div = document.createElement('div');
    div.appendChild(document.createTextNode(str));
    return div.innerHTML;
  }

  // --- Render result cards ---
  function renderResults(scored) {
    var top = [];
    for (var i = 0; i < scored.length && top.length < 5; i++) {
      if (scored[i].total > 0) top.push(scored[i]);
    }

    // Fallback: if no products above threshold, show top 3
    if (top.length === 0 && scored.length > 0) {
      for (var f = 0; f < scored.length && top.length < 3; f++) {
        top.push(scored[f]);
      }
    }

    resultsGrid.innerHTML = '';

    if (top.length === 0) {
      resultsGrid.innerHTML =
        '<div class="ame-lc-cfg__no-match">' +
          '<p>No exact matches found for your criteria.</p>' +
          '<p>Try adjusting your preferences or <a href="tel:+18039261493">call (803) 926-1493</a> for expert help.</p>' +
        '</div>';
      resultsSubtitle.textContent = '';
      announce('No exact matches found. Try adjusting your preferences or call for help.');
      return top;
    }

    if (top[0].total <= 0) {
      resultsSubtitle.textContent =
        'We couldn\'t find an exact match, but here are our closest options. ' +
        'Call (803) 926-1493 for personalized help.';
    } else {
      resultsSubtitle.textContent = 'Based on your answers, here are our top ' + top.length + ' recommendations.';
    }

    for (var j = 0; j < top.length; j++) {
      var p = top[j].product;
      var specs = buildSpecs(p);
      var specsHtml = '';
      for (var k = 0; k < specs.length; k++) {
        specsHtml += '<span class="ame-lc-cfg__card-spec">' + escapeHtml(specs[k]) + '</span>';
      }

      var priceHtml = '';
      if (p.call_for_price) {
        priceHtml = '<span style="color:var(--ame-primary)">Call for Price</span>';
      } else {
        priceHtml = formatPrice(p.price);
        if (p.compare_at_price && p.compare_at_price > p.price) {
          priceHtml += ' <span class="ame-card__compare-price">' + formatPrice(p.compare_at_price) + '</span>';
        }
      }

      var vendorHtml = p.vendor ? '<span class="ame-card__vendor">' + escapeHtml(p.vendor) + '</span>' : '';

      var card =
        '<a href="' + escapeHtml(p.url) + '" class="ame-card">' +
          '<div class="ame-card__image-wrap">' +
            '<span class="ame-lc-cfg__match-badge">' + getMatchLabel(j) + '</span>' +
            (p.image ? '<img class="ame-card__image" src="' + escapeHtml(p.image) + '" alt="' + escapeHtml(p.title) + '" loading="lazy" width="500" height="500">' : '') +
          '</div>' +
          '<div class="ame-card__body">' +
            vendorHtml +
            '<h3 class="ame-card__title">' + escapeHtml(p.title) + '</h3>' +
            '<div class="ame-lc-cfg__card-specs">' + specsHtml + '</div>' +
            '<div class="ame-card__price">' + priceHtml + '</div>' +
          '</div>' +
        '</a>';

      resultsGrid.insertAdjacentHTML('beforeend', card);
    }

    announce('Found ' + top.length + ' matching lift chairs. Showing your top recommendations.');
    return top;
  }

  // --- Build filter URL for "View All" ---
  function buildFilterUrl(ans) {
    var base = '/collections/lift-chairs';
    var params = [];

    // Position type filter
    if (ans.lc_position) {
      switch (ans.lc_position) {
        case 'full_recline':
          params.push('filter.p.m.custom.position_type=Infinite+Position');
          break;
        case 'occasional':
          params.push('filter.p.m.custom.position_type=Infinite+Position');
          break;
      }
    }

    // Budget filter
    if (ans.lc_budget) {
      switch (ans.lc_budget) {
        case '1000':
          params.push('filter.v.price.lte=1000');
          break;
        case '2000':
          params.push('filter.v.price.gte=1000');
          params.push('filter.v.price.lte=2000');
          break;
        case '3000':
          params.push('filter.v.price.gte=2000');
          params.push('filter.v.price.lte=3000');
          break;
        case '9999':
          params.push('filter.v.price.gte=3000');
          break;
      }
    }

    return params.length > 0 ? base + '?' + params.join('&') : base;
  }

  // --- Show results ---
  function showResults() {
    wizard.hidden = true;
    var scored = scoreProducts(answers);
    renderResults(scored);

    if (viewAllLink) viewAllLink.href = buildFilterUrl(answers);

    resultsPanel.classList.add('is-active');
    if (resultsHeading) resultsHeading.focus();

    clearState();
  }

  // --- Event: CTA click ---
  ctaBtn.addEventListener('click', function () {
    if (wizard.hidden && !resultsPanel.classList.contains('is-active')) {
      openWizard();
    } else {
      closeWizard();
    }
  });

  // --- Event: radio/checkbox change ---
  root.addEventListener('change', function (e) {
    var stepEl = e.target.closest('[data-lc-cfg-step]');
    if (stepEl) updateNextBtn(stepEl);
  });

  // --- Event: next ---
  root.addEventListener('click', function (e) {
    var nextBtn = e.target.closest('[data-lc-cfg-next]');
    if (!nextBtn || nextBtn.disabled) return;

    collectAnswer();

    if (currentStep < TOTAL_STEPS) {
      showStep(currentStep + 1);
    } else {
      showResults();
    }
  });

  // --- Event: back ---
  root.addEventListener('click', function (e) {
    if (e.target.closest('[data-lc-cfg-back]')) {
      collectAnswer();
      if (currentStep > 1) {
        showStep(currentStep - 1);
      }
    }
  });

  // --- Event: close ---
  root.addEventListener('click', function (e) {
    if (e.target.closest('[data-lc-cfg-close]')) {
      closeWizard();
    }
  });

  // --- Event: restart ---
  root.addEventListener('click', function (e) {
    if (e.target.closest('[data-lc-cfg-restart]')) {
      resultsPanel.classList.remove('is-active');
      answers = {};
      currentStep = 1;

      // Clear all inputs
      var inputs = root.querySelectorAll('input[type="radio"], input[type="checkbox"]');
      for (var i = 0; i < inputs.length; i++) {
        inputs[i].checked = false;
      }

      clearState();
      openWizard();
    }
  });

  // --- Event: Banner CTA (button in ame-collection-banner) ---
  var bannerCta = document.querySelector('[data-lc-cfg-banner-cta]');
  if (bannerCta) {
    bannerCta.addEventListener('click', function () {
      openWizard();
    });
  }

  // --- Event: Escape key ---
  document.addEventListener('keydown', function (e) {
    if ((e.key === 'Escape' || e.key === 'Esc') && !wizard.hidden) {
      closeWizard();
    }
  });

  // --- Restore session ---
  var saved = loadState();
  if (saved && saved.step && saved.answers) {
    answers = saved.answers;
    currentStep = saved.step;

    // Restore radio selections
    var fieldNames = Object.keys(answers);
    for (var f = 0; f < fieldNames.length; f++) {
      var selector = 'input[name="' + fieldNames[f] + '"][value="' + answers[fieldNames[f]] + '"]';
      var input = root.querySelector(selector);
      if (input) input.checked = true;
    }

    openWizard();
  }
})();
